import "../../moneyReports.css"
import { useState, useEffect, useContext } from "react"
import AuthContext from "contexte/AuthContext"
import { useTranslation } from "react-i18next"
import DashboardLayout from "components/layout/dashboard/DashboardLayout"
import FilterComponent from "components/Filters/FilterComponent/FilterComponent"
import PageLoader from "components/pageLoader/PageLoader"
import Tableau from "components/DataComponents/Tableau/Tableau"
import { fetchProfitsPerStock } from "services/fetchMoneyReports"
import { formatDate, RELEASE_DATE } from "utils/date"
import { Amount, EmptyState, ErrorState, Pager, ReportHeader, SummaryTiles, formatEur } from "../../MoneyReportParts"

// pos renvoyé par l'API : '-1' total de la période, '0' ligne de période, sinon un ticker de cette période
const TOTAL_POS = '-1';
const PERIOD_POS = '0';

const ProfitsPerStock = () => {
    const { user } = useContext(AuthContext);
    const { t } = useTranslation();

    const [rows, setRows] = useState([]);
    const [totals, setTotals] = useState(null);
    const [rowsCount, setRowsCount] = useState(0);
    const [timeframe, setTimeframe] = useState('day');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [selectedDate, setSelectedDate] = useState({
        date1: { startDate: new Date(RELEASE_DATE), endDate: new Date() },
        date2: { startDate: null, endDate: null },
    });
    const [limit, setLimit] = useState(10);
    const [page, setPage] = useState(0);

    const fetchData = async () => {
        try {
            setError(false);
            const response = await fetchProfitsPerStock({
                user, limit, page, sort: timeframe,
                startDate: selectedDate.date1.startDate, endDate: selectedDate.date1.endDate
            });
            const totalRow = response.find(row => row.pos === TOTAL_POS);
            if (totalRow) setTotals(totalRow);
            else if (page === 0) setTotals(null);
            setRows(response.filter(row => row.pos !== TOTAL_POS));
            setRowsCount(response.length);
        } catch (e) {
            console.log(e);
            setError(true);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (selectedDate.date1.startDate !== null && selectedDate.date1.endDate !== null) {
            fetchData();
        }
    }, [selectedDate, page, limit, timeframe]);

    const handleDate = (newDate) => {
        setSelectedDate(newDate);
        setPage(0);
    }

    const handleLimit = (newLimit) => {
        setLimit(newLimit);
        setPage(0);
    }

    const handleTimeframe = (newTimeframe) => {
        setTimeframe(newTimeframe);
        setPage(0);
    }

    const computeDateFormat = (date) => {
        switch (timeframe) {
            case 'day':
                return formatDate({ date, month: 'long' });
            case 'month':
                return formatDate({ date, month: 'long', noDay: true });
            case 'year':
                return formatDate({ date, noDay: true, noMonth: true });
            default:
                return 'ERROR'
        }
    }

    const tbody = rows.map(item => {
        const isPeriod = item.pos === PERIOD_POS;
        return {
            data: [
                isPeriod ? <span className="money_report_group">{computeDateFormat(item.date)}</span> : '',
                isPeriod ? '' : <span className="money_report_ticker">{item.ticker}</span>,
                <Amount value={item.profit} strong={isPeriod} />,
                <Amount value={item.platform} strong={isPeriod} />,
                <Amount value={item.fx} strong={isPeriod} />
            ]
        };
    });

    const tiles = totals ? [
        { label: 'Commissions sur la période', value: formatEur(totals.profit), main: true },
        { label: 'Commissions Trade', value: formatEur(totals.platform) },
        { label: 'Commissions FX', value: formatEur(totals.fx) },
    ] : [];

    return (
        <DashboardLayout page={t("pages_name.report_profit_per_stock")}>
            <div className="money_report_page">
                <ReportHeader
                    title="Profits par action"
                    hint="Commissions par période, puis le détail par ticker de chaque période."
                />

                <div className="money_report_toolbar">
                    <FilterComponent
                        date={true}
                        limit={true}
                        fetchLimit={limit}
                        setFetchLimit={handleLimit}
                        timeframeFilter={timeframe}
                        setTimeframeFilter={handleTimeframe}
                        selectedDate={selectedDate}
                        setSelectedDate={handleDate}
                    />
                </div>

                {
                    loading ? (
                        <div className='pageLoaderBox'>
                            <PageLoader />
                        </div>
                    ) : error ? (
                        <ErrorState title="Erreur lors du chargement des profits par action" onRetry={fetchData} />
                    ) : (
                        <>
                            {totals && <SummaryTiles tiles={tiles} />}
                            <div className="money_report_card">
                                {
                                    tbody.length || page > 0 ? (
                                        <>
                                            <Tableau
                                                thead={['Période', 'Ticker', 'Commissions', 'Commissions Trade', 'Commissions FX']}
                                                tbody={tbody}
                                            />
                                            <Pager page={page} setPage={setPage} hasNextPage={rowsCount >= limit} />
                                        </>
                                    ) : (
                                        <EmptyState title="Aucune commission sur la période" hint="Élargissez les dates pour voir les commissions." />
                                    )
                                }
                            </div>
                        </>
                    )
                }
            </div>
        </DashboardLayout>
    )
}

export default ProfitsPerStock
